/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import {
	ISchemaStringWithConstConstItem,
	ISchemaStringWithConstModel,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { getInspectorControls } from "./inspectorControls";
import { useTranslate } from "@adapptio/lib-components";
import { FieldDropdown, TFieldDropdownOption } from "../../Fields/FieldDropdown";
import { InspectorControl } from "../InspectorControl";
import { FieldColor } from "../../Fields/FieldColor";
import { FieldFontSize } from "../../Fields/FieldFontSize";
import { useEditorInspectorContext } from "../../../contexts/EditorInspectorContext";

export const ControlStringWithConst = (_model: TGenericModelNode): IInspectorControls => {
	const model = _model as ISchemaStringWithConstModel;

	const valueControls = getInspectorControls(model.value);

	return {
		...valueControls,
		primary: valueControls.primary
			? {
					component: ControlStringWithConst_Primary,
					model: model
			  }
			: null,
		dependencyModels: [ model.value, ...(valueControls.dependencyModels || []) ]
	};
};

export const ControlStringWithConst_PrimaryDropdown: TInspectorControlComponent<ISchemaStringWithConstModel> =
	({ model: rootModel, id }) => {
		const t = useTranslate();
		const { model: valueModel, validationErrors } = useBlueprintModel(
			rootModel.value.constant,
			"constString"
		);

		const inspectorContext = useEditorInspectorContext();

		const options = useMemo(() => {
			return rootModel.schema.opts.constants.map((item) => {
				return {
					label: t("editor", item.label),
					value: String(item.value)
				} as TFieldDropdownOption;
			});
		}, [ rootModel ]);

		const handleChange = useCallback(
			(value: string) => {
				if (!valueModel) {
					return;
				}

				valueModel.schema.setValue(valueModel, value, true);
			},
			[ valueModel ]
		);

		if (!valueModel) {
			return <InvalidModelNode model={valueModel} />;
		}

		return (
			<FieldDropdown
				id={id}
				value={String(valueModel.value)}
				options={options}
				onChange={handleChange}
				validationErrors={validationErrors}
				allowCustomInput={true}
				grow={true}
				readOnly={inspectorContext?.readOnly}
			/>
		);
	};

export const ControlStringWithConst_PrimaryColor: TInspectorControlComponent<ISchemaStringWithConstModel> = ({
	model: rootModel,
	id,
	label
}) => {
	const t = useTranslate();
	const { model: valueModel, validationErrors } = useBlueprintModel(
		rootModel.value.constant,
		"constString"
	);

	const inspectorContext = useEditorInspectorContext();

	const constants = useMemo(() => {
		return rootModel.schema.opts.constants.map((item) => {
			return {
				label: t("editor", item.label),
				value: item.value
			} as ISchemaStringWithConstConstItem;
		});
	}, [ rootModel ]);

	const handleChange = useCallback(
		(value: string) => {
			if (!valueModel) {
				return;
			}

			valueModel.schema.setValue(valueModel, value, true);
		},
		[ valueModel ]
	);

	if (!valueModel) {
		return <InvalidModelNode model={valueModel} />;
	}

	return (
		<FieldColor
			id={id}
			label={label}
			value={String(valueModel.value)}
			constants={constants}
			onChange={handleChange}
			validationErrors={validationErrors}
			grow={true}
			readOnly={inspectorContext?.readOnly}
		/>
	);
};

export const ControlStringWithConst_PrimaryFontSize: TInspectorControlComponent<ISchemaStringWithConstModel> =
	({ model: rootModel }) => {
		const t = useTranslate();
		const { model: valueModel, validationErrors } = useBlueprintModel(
			rootModel.value.constant,
			"constString"
		);

		const inspectorContext = useEditorInspectorContext();

		const constants = useMemo(() => {
			return rootModel.schema.opts.constants.map((item) => {
				return {
					label: t("editor", item.label),
					value: item.value
				} as ISchemaStringWithConstConstItem;
			});
		}, [ rootModel ]);

		const handleChange = useCallback(
			(value: string) => {
				if (!valueModel) {
					return;
				}

				valueModel.schema.setValue(valueModel, value, true);
			},
			[ valueModel ]
		);

		if (!valueModel) {
			return <InvalidModelNode model={valueModel} />;
		}

		return (
			<FieldFontSize
				value={String(valueModel.value)}
				constants={constants}
				onChange={handleChange}
				validationErrors={validationErrors}
				grow={true}
				readOnly={inspectorContext?.readOnly}
			/>
		);
	};

export const ControlStringWithConst_Primary: TInspectorControlComponent<ISchemaStringWithConstModel> = ({
	model: _model,
	path,
	depth,
	id,
	label
}) => {
	const { model } = useBlueprintModel(_model, "stringWithConst");

	const valueControls = getInspectorControls(model?.value);

	const overrideControls = useMemo(() => {
		if (!model) {
			return {};
		}

		// @todo Check model.schema.editorOptions.controlType
		if (model.schema.opts.editorOptions?.controlType === "colorSelector") {
			return {
				primary: {
					component: ControlStringWithConst_PrimaryColor,
					model: model
				}
			};
		} else if (model.schema.opts.editorOptions?.controlType === "fontSizeSelector") {
			return {
				primary: {
					component: ControlStringWithConst_PrimaryFontSize,
					model: model
				}
			};
		} else {
			return {
				primary: {
					component: ControlStringWithConst_PrimaryDropdown,
					model: model
				}
			};
		}
	}, [ model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<InspectorControl
			id={id}
			label={label}
			config={valueControls.primary}
			path={path}
			depth={depth}
			overrideControls={overrideControls}
		/>
	);
};
